import React from "react";
import { HomeOutlined, UserOutlined, SearchOutlined } from "@ant-design/icons";
import { Breadcrumb, Form, Input, Select, Table, Button, Space, notification, Modal } from "antd";
import type { ColumnType } from "antd/es/table";
import type { FilterConfirmProps } from "antd/es/table/interface";
import "./data_supply_slip_return.css";
import useSWR from "swr";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../context/AuthContext";

const fetcher = (url: string) => axios.get(url).then((res) => res.data);
const DataSupplySlipReturn: React.FC = () => {
    const navigate = useNavigate();
    const { userData } = useAuth();
    const transformStyle = {
        transform: "scale(0.80)",
        transformOrigin: "top left",
        width: "125%",
    };

    interface DataType {
        id: number;
        status: string;
        supply_slip_return_no: string;
        supply_slip_return_date: string;
        work_order_no: string;
        work_order_date: string;
        warehouse: string;
        no_polisi: string;
    }

    interface WarehouseType { warehouse_code: string; warehouse_name: string; }
    const [searchText, setSearchText] = React.useState('');
    const [searchedColumn, setSearchedColumn] = React.useState('');
    const searchInput = React.useRef<any>(null);

    const [pagination, setPagination] = React.useState({ current: 1, pageSize: 10 });
    const [sortParams, setSortParams] = React.useState({ field: 'id', order: 'descend' });
    const { data: apiResponse, isLoading, mutate } = useSWR<{ data: DataType[], total: number }>(
        `${process.env.REACT_APP_BACKEND_URL}/api/supply-slip-return/list?page=${pagination.current}&limit=${pagination.pageSize}&sortBy=${sortParams.field}&sortOrder=${sortParams.order === 'ascend' ? 'ASC' : 'DESC'}&search=${searchText}&warehouse=${userData?.warehouse_code || ''}`, // ✅: request filter warehouse ke server
        fetcher
    );
    const { data: warehouseData } = useSWR<WarehouseType[]>(`${process.env.REACT_APP_BACKEND_URL}/api/warehouse/list`, fetcher);
    const handleSearch = (selectedKeys: string[], confirm: (param?: FilterConfirmProps) => void, dataIndex: string) => { confirm(); setSearchText(selectedKeys[0]); setSearchedColumn(dataIndex); setPagination({ ...pagination, current: 1 }); };
    const handleReset = (clearFilters: () => void) => { clearFilters(); setSearchText(''); };
    const hasActionAuthority = userData?.otoritas ? userData.otoritas.split(';').includes('03') : false;
    const handleUpdateStatus = async (record: DataType, newStatus: string) => {
        try {
            const itemsRes = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/supply-slip-return/items-return/${record.supply_slip_return_no}`);
            const payload = {
                header: { ...record, status: newStatus },
                items: itemsRes.data
            };
            await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/supply-slip-return/save`, payload);
            notification.success({ message: 'Berhasil', description: `Status berhasil diubah.` });
            mutate();
        } catch (error) {
            notification.error({ message: 'Gagal', description: 'Terjadi kesalahan saat mengubah status.' });
        }
    };

    const handleApprove = async (record: DataType) => {
        try {
            const checkInvoice = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/supply-slip-return/check-invoice/${record.work_order_no}`);
            if (checkInvoice.data && (checkInvoice.data.status === 22 || checkInvoice.data.status === '22')) {
                return notification.warning({ message: 'Peringatan', description: 'Tidak dapat approve karena sudah terinvoice!' });
            }
            const itemsRes = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/supply-slip-return/items-return/${record.supply_slip_return_no}`);
            const woItemsRes = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/work-order/items/${record.work_order_no}`);
            const isAllSparepartsReturned = woItemsRes.data
                .filter((item: any) => String(item.type) === '1')
                .every((woItem: any) => {
                    const dbSupplyQty = Number(woItem.supply_qty || 0);
                    if (dbSupplyQty <= 0 || woItem.status === 'R') return true;
                    const returnItem = itemsRes.data.find((ri: any) => ri.item_code === woItem.item_code && String(ri.type) === '1');
                    return returnItem && Number(returnItem.qty_return) === dbSupplyQty;
                });
            const allItemsWillBeR = isAllSparepartsReturned;
            const proceedApproval = async (finalStatus: string) => {
                const payload = {
                    header: { ...record, status: finalStatus },
                    items: itemsRes.data.map((item: any) => ({ ...item, work_order_no: record.work_order_no, qty_to_reduce: item.qty_return })),
                };
                await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/supply-slip-return/submit`, payload);
                notification.success({ message: 'Berhasil', description: 'Data Return berhasil disetujui dan stok diperbarui.' });
                mutate();
            };
            await proceedApproval('4');
        } catch (error) {
            notification.error({ message: 'Gagal', description: 'Terjadi kesalahan saat memproses approval.' });
        }
    };
    const getColumnSearchProps = (dataIndex: keyof DataType): ColumnType<DataType> => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
            <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
                <Input
                    ref={searchInput}
                    placeholder={`Search ${dataIndex}`}
                    value={selectedKeys[0]}
                    onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                    style={{ marginBottom: 8, display: 'block' }} />
                <Space>
                    <Button type="primary" onClick={() => handleSearch(selectedKeys as string[], confirm, dataIndex)} icon={<SearchOutlined />} size="small" style={{ width: 90 }}>Search</Button>
                    <Button onClick={() => clearFilters && handleReset(clearFilters)} size="small" style={{ width: 90 }}>Reset</Button>
                </Space>
            </div>
        ),
        filterIcon: (filtered: boolean) => <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />,
        onFilter: (value, record) => record[dataIndex].toString().toLowerCase().includes((value as string).toLowerCase()),
    });

    const columns: ColumnType<DataType>[] = [
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            sorter: (a, b) => a.status.localeCompare(b.status),
            ...getColumnSearchProps('status'),
            render: (val) => {
                if (String(val) === '1') return 'Draft';
                if (String(val) === '2') return 'Waiting for Approved';
                if (String(val) === '4') return 'Complete';
                if (String(val) === '5') return 'Canceled';
                if (String(val) === '99') return 'Cancel Return';
                return val;
            }
        },
        { title: 'Supply Slip Return No', dataIndex: 'supply_slip_return_no', key: 'supply_slip_return_no', sorter: (a, b) => a.supply_slip_return_no.localeCompare(b.supply_slip_return_no), ...getColumnSearchProps('supply_slip_return_no') },
        { title: 'Supply Slip Return Date', dataIndex: 'supply_slip_return_date', key: 'supply_slip_return_date', sorter: (a, b) => a.supply_slip_return_date.localeCompare(b.supply_slip_return_date), ...getColumnSearchProps('supply_slip_return_date') },
        { title: 'Work Order No', dataIndex: 'work_order_no', key: 'work_order_no', sorter: (a, b) => a.work_order_no.localeCompare(b.work_order_no), ...getColumnSearchProps('work_order_no') },
        { title: 'Work Order Date', dataIndex: 'work_order_date', key: 'work_order_date', sorter: (a, b) => a.work_order_date.localeCompare(b.work_order_date), ...getColumnSearchProps('work_order_date') },
        {
            title: 'Warehouse',
            dataIndex: 'warehouse',
            key: 'warehouse',
            sorter: (a, b) => a.warehouse.localeCompare(b.warehouse),
            ...getColumnSearchProps('warehouse'),
            render: (warehouseCode: string) => {
                const warehouse = warehouseData?.find((w) => w.warehouse_code === warehouseCode);
                return warehouse ? warehouse.warehouse_name : warehouseCode;
            },
        },
    ];

    return (

        <div id="i3b9" style={transformStyle}>
            <div className="gjs-grid-row" id="if0b">
                <div className="gjs-grid-column" id="ivnr">
                    <Breadcrumb
                        items={[
                            {
                                title: <HomeOutlined />,
                            },
                            {
                                title: (
                                    <>
                                        <span>Data Supply Slip Return</span>
                                    </>
                                ),
                            },
                            {
                                href: "#/dashboard/supply_slip_return",
                                title: "Supply Slip Return",
                            },
                        ]}
                    />
                </div>
            </div>
            <div className="gjs-grid-row" id="id949">
                <div className="gjs-grid-column" id="ijx4b">
                    <Table<DataType>
                        columns={columns}
                        dataSource={apiResponse?.data || []}
                        rowKey="id"
                        loading={isLoading}
                        pagination={{
                            current: pagination.current,
                            pageSize: pagination.pageSize,
                            total: apiResponse?.total || 0,
                            showSizeChanger: true,
                        }}
                        onChange={(p, f, s: any) => {
                            setPagination({ current: p.current || 1, pageSize: p.pageSize || 10 });
                            if (s && s.field) {
                                if (s && s.field) {
                                    setSortParams({ field: s.field, order: s.order || 'descend' });
                                }
                            }
                        }}
                        scroll={{ x: 'max-content' }}
                        onRow={(record) => ({
                            onDoubleClick: () => {
                                const displayStatus =
                                    String(record.status) === '1' ? 'Draft' :
                                        (String(record.status) === '2' ? 'Waiting for Approved' :
                                            (String(record.status) === '4' ? 'Complete' : (String(record.status) === '5' ? 'Canceled' : record.status)));
                                navigate("/dashboard/supply_slip_return", { state: { record: { ...record, status: displayStatus } } });
                            },
                            style: { cursor: 'pointer' }
                        })}
                    />
                </div>
            </div>
        </div>
    );
};

export default DataSupplySlipReturn;